import { Box, Container, Heading, SimpleGrid, Text, Icon, Card, useColorModeValue, VStack } from '@chakra-ui/react'
import { FaCity, FaBuilding, FaBus, FaComments, FaTools, FaSearch } from 'react-icons/fa'
import { useNavigate, Link } from 'react-router-dom'

interface OptionCardProps {
  title: string
  description: string | JSX.Element
  icon: typeof FaCity
  onClick?: () => void
}

const OptionCard = ({ title, description, icon, onClick }: OptionCardProps) => (
  <Card
    variant="elevated"
    cursor="pointer"
    h="full"
    bg={useColorModeValue('whiteAlpha.900', 'blackAlpha.800')}
    role="button"
    aria-label={title}
    onClick={onClick}
    position="relative"
    overflow="hidden"
    backdropFilter="blur(10px)"
    border="1px solid"
    borderColor={useColorModeValue('whiteAlpha.300', 'whiteAlpha.100')}
  >
    <Box
      position="absolute"
      top="-20px"
      right="-20px"
      w="120px"
      h="120px"
      bg={useColorModeValue('brand.50', 'whiteAlpha.50')}
      borderRadius="full"
      opacity="0.6"
      transition="transform 0.3s ease"
      _groupHover={{ transform: 'scale(1.2)' }}
    />
    <VStack spacing={6} p={8} align="flex-start" position="relative">
      <Icon 
        as={icon} 
        boxSize={12}
        color="brand.500"
        transition="all 0.3s ease"
        _groupHover={{ 
          transform: 'scale(1.1) rotate(5deg)',
          color: 'brand.400'
        }}
      />
      <Box>
        <Heading 
          size="md" 
          mb={3}
          color={useColorModeValue('gray.800', 'white')}
          _groupHover={{ color: 'brand.500' }}
        >
          {title}
        </Heading>
        <Text
          color={useColorModeValue('gray.600', 'gray.300')}
          fontSize="md"
          lineHeight="tall"
        >
          {description}
        </Text>
      </Box>
    </VStack>
  </Card>
)

export default function Home() {
  const navigate = useNavigate();

  const handleOptionClick = (title: string) => {
    if (title === 'Consultar Estado') {
      navigate('/consulta-expediente');
    } else {
      // Redirigir al formulario con el tipo de denuncia
      navigate('/form', { state: { tipoBarrera: title } });
    }
  };
  const options = [
    {
      title: 'Barreras Urbanísticas',
      description: 'Completa el formulario para reportar una barrera urbanística',
      icon: FaCity
    },
    {
      title: 'Barreras de la Edificación',
      description: 'Reporta barreras en edificios públicos o privados',
      icon: FaBuilding
    },
    {
      title: 'Transporte',
      description: 'Reporta problemas relacionados con los medios de transporte.',
      icon: FaBus
    },
    {
      title: 'Barreras de la Comunicación',
      description: 'Reporta barreras en los medios o sistemas de comunicación',
      icon: FaComments
    },
    {
      title: 'Ayudas Técnicas y Consultas',
      description: 'Solicita información de asistencia técnica o realiza una consulta general',
      icon: FaTools
    },
    {
      title: 'Consultar Estado',
      description: 'Consulta el estado de una solicitud previamente presentada',
      icon: FaSearch
    }
  ]

  return (
    <Box 
      minH="100vh" 
      backgroundImage="url('/silla.jpg')"
      backgroundSize="cover"
      backgroundPosition="center"
      position="relative"
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="whiteAlpha.700"
      />
      
      <Container maxW="container.xl" py={20} position="relative" zIndex={1}>
        <VStack spacing={12} align="stretch">
          <VStack spacing={6} align="stretch">
            <Box 
              position="relative"
              bg="whiteAlpha.500"
              p={{ base: 2, md: 3 }}
              borderRadius="xl"
              backdropFilter="blur(10px)"
            >
              <Box 
                position="absolute"
                left={{ base: 2, md: 3 }}
                top="50%"
                transform="translateY(-50%)"
              >
                <Link to="/">
                  <Box 
                    as="img" 
                    src="/famma.png" 
                    alt="Logo Famma" 
                    className="logo" 
                    h={{ base: "45px", md: "55px" }}
                    objectFit="contain"
                    transition="transform 0.2s ease"
                    _hover={{ transform: 'scale(1.05)' }}
                  />
                </Link>
              </Box>
              <Heading
                as="h1"
                fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }}
                fontWeight="bold"
                color="brand.600"
                letterSpacing="tight"
                lineHeight="shorter"
                textAlign="center"
                px={{ base: "60px", md: "80px" }}
              >
                Denuncias de Accesibilidad
              </Heading>
            </Box>
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              color="gray.700"
              textAlign="center"
              fontWeight="semibold"
              bg="whiteAlpha.600"
              py={2}
              px={4}
              borderRadius="lg"
              backdropFilter="blur(10px)"
            >
              Selecciona el tipo de denuncia que deseas presentar
            </Text>
          </VStack>

          <SimpleGrid 
            columns={{ base: 1, md: 2, lg: 3 }} 
            gap={8}
            px={{ base: 4, md: 0 }}
          >
            {options.map((option, index) => (
              <OptionCard 
                key={index} 
                {...option} 
                onClick={() => handleOptionClick(option.title)}
              />
            ))}
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>
  )
}
